import * as React from 'react';
import * as acl from 'common/utils/acl';

import { Block, Row } from 'emotion-jsxstyle';
import { EntityActions, EntityType } from 'common/entity';

import { Member } from 'common/api/models/Member';
import { ChapterPosition } from 'common/api/models/ChapterPosition';
import { UserPermissions } from 'common/api/models/UserPermissions';
import { Order } from 'common/api/models/Order';
import { Link, RouteComponentProps } from 'react-router-dom';
import { ActivityFeed } from 'common/components/activity/ActivityFeed';
import { AppState } from 'common/models/AppState';
import { Badge } from 'common/components/Badge';
import { Card } from 'common/components/Card';
import { CertDialog } from './CertDialog';
import { ConfirmDialog } from 'common/components/dialogs/ConfirmDialog';
import { Loading } from 'common/components/Loading';
import { MediaImage } from 'common/components/MediaImage';
import { MemberDialog } from 'common/components/dialogs/MemberDialog';
import { Page } from 'common/components/layout/Page/Page';
import { toApiError } from 'common/utils/api';
import { MemberApi } from 'common/api/resources/MemberApi';
import { OrderApi } from 'common/api/resources/OrderApi';
import { HistoryTabs } from 'common/components/HistoryTabs';
import { TabPane } from 'reactstrap';
import { MembersOrders } from '../Members/MembersOrders';
import { MembersCertifications } from '../Members/MembersCertifications';
import { MembersBadges } from '../Members/MembersBadges';
import { UsersDetailInfo } from './UsersDetailInfo';
import { Analytics } from 'common/utils/Analytics';
import { DialogResendEligibleEmail } from 'common/components/dialogs/DialogResendEligibleEmail';
import { ChapterPositionApi } from 'common/api/resources/ChapterPositionApi';
import { MemberLDActionDialog } from 'common/components/dialogs/MemberLDActionDialog';
import { ActionButton } from 'common/components/layout/Page/ActionButton';
import { ProductApi } from 'common/api/resources/ProductApi';
import { Product } from 'common/api/models/Product';

export interface UsersDetailProps {
  appState: AppState;
  entityActions: EntityActions;
  number: string;
  reloadAt: Date;
  onEdit: (user: Member) => void;
}
export interface UsersDetailState {
  member?: Member;
  orders?: Order[];
  chapterPosition: ChapterPosition[];
  editMember?: Member;
  editAction?: Member;
  addCertMember?: Member;
  error?: string;
  dialog?: string;
  dialogSuccess?: string;
  dialogError?: string;
  dialogLoading?: boolean;
  isLoading: boolean;
  allProducts: Product[];
}

export class UsersDetail extends React.Component<UsersDetailProps & RouteComponentProps<{}>, UsersDetailState> {
  state: UsersDetailState = {
    chapterPosition: [],
    isLoading: false,
    allProducts: [],
  };

  componentDidMount() {
    this.reload(this.props.number);
  }

  componentDidUpdate(prevProps: UsersDetailProps) {
    if (prevProps.number !== this.props.number || prevProps.reloadAt !== this.props.reloadAt) {
      this.reload(this.props.number);
    }
  }

  reload(num: string) {
    MemberApi.getMemberByNumber(Number(num), { expandTags: true }).then(
      member => {
        this.setState({ member });
        this.props.entityActions.fetchIfPresent(EntityType.MEMBER, member.createId);
        this.props.entityActions.fetchIfPresent(EntityType.MEMBER, member.updateId);
        member.orgs!.map(o => this.props.entityActions.fetch(EntityType.MEMBER, o.memberId!));
        OrderApi.getOrders({ memberId: member.id }).then(
          res => {
            this.setState({ orders: res.data });
          },
          err => {
            this.setState({ error: toApiError(err) });
          }
        );
        Promise.all([
          ChapterPositionApi.getChapterPositions({
            memberId: member.id!,
            roleName: Member.ChapterRole.OFFICER,
          }),
        ]).then(
          ([chapterPos]) => {
            // console.log(memberRes);
            this.setState({ chapterPosition: chapterPos.data });
            console.log(this.state.chapterPosition.length);
          },
          err => {
            this.setState({ error: toApiError(err) });
          }
        );
      },
      err => {
        this.setState({ error: toApiError(err) });
      }
    );
    ProductApi.getAllProducts({ type: 'LEARNING' }).then(
      res => {
        this.setState({ allProducts: res.data, isLoading: false });
      },
      error => this.setState({ error: toApiError(error), isLoading: false })
    );
  }

  handleReload = () => {
    this.reload(this.props.number);
  };

  handleClickEdit = () => {
    this.props.onEdit(this.state.member!);
  };

  handleClickAction = () => {
    this.props.onEdit(this.state.member!);
  };

  handleOpenActivate = () => {
    this.setState({ dialog: 'activate' });
  };

  handleActivate = () => {
    MemberApi.activateMember(this.state.member!.id!).then(
      res => {
        this.setState({ dialog: undefined }, () => {
          this.reload(this.props.number);
        });
        Analytics.member.update();
      },
      err => {
        this.setState({ dialogError: toApiError(err), dialogSuccess: undefined, dialogLoading: false });
      }
    );
  };

  handleOpenSendReset = () => {
    this.setState({
      dialog: 'sendReset',
    });
  };
  handleOpenLDDenyEmail = () => {
    this.setState({
      dialog: 'sendApplicationDenied',
    });
  };

  handleOpenSendEligibleEmail = () => {
    this.setState({
      dialog: 'sendEligibleEmail',
    });
  };

  handleSendReset = () => {
    const user = this.state.member;
    if (user && user.email) {
      this.setState({ dialogLoading: true });
      MemberApi.sendResetEmail({ email: user.email }).then(
        res => {
          this.setState({
            dialogSuccess: 'Email sent to ' + user.email + '.',
            dialogError: undefined,
            dialogLoading: false,
          });
          Analytics.member.resetPassword();
        },
        err => {
          this.setState({ dialogError: toApiError(err), dialogSuccess: undefined, dialogLoading: false });
        }
      );
    }
  };
  handleSendLDDenyEmail = () => {
    const user = this.state.member;
    if (user && user.email) {
      this.setState({ dialogLoading: true });
      MemberApi.sendLearndashDenial({ email: user.email }).then(
        res => {
          this.setState({
            dialogSuccess: 'Email sent to ' + user.email + '.',
            dialogError: undefined,
            dialogLoading: false,
          });
        },
        err => {
          this.setState({ dialogError: toApiError(err), dialogSuccess: undefined, dialogLoading: false });
        }
      );
    }
  };

  handleUpdateTags = (tagIds: string[]) => {
    if (!this.state.member) {
      return;
    }
    MemberApi.updateMember(this.state.member.id!, { tagIds: tagIds }).then(
      res => {
        this.reload(this.props.number);
        Analytics.member.update();
      },
      err => {
        this.setState({ error: toApiError(err) });
      }
    );
  };

  handleUpdateInternalTags = (tagIds: string[]) => {
    if (!this.state.member) {
      return;
    }
    MemberApi.updateMember(this.state.member.id!, { internalTagIds: tagIds }).then(
      res => {
        this.reload(this.props.number);
        Analytics.member.update();
      },
      err => {
        this.setState({ error: toApiError(err) });
      }
    );
  };

  handleUpdateAssignedTags = (tagIds: string[]) => {
    if (!this.state.member) {
      return;
    }
    MemberApi.updateMember(this.state.member.id!, {
      assignedTags: tagIds.map(tagId => {
        return { tagId };
      }),
    }).then(
      res => {
        this.reload(this.props.number);
        Analytics.member.update();
      },
      err => {
        this.setState({ error: toApiError(err) });
      }
    );
  };

  handleCreateMember = () => {
    this.setState({
      editMember: {
        name: this.state.member!.firstName + ' ' + this.state.member!.lastName,
        memberType: Member.MemberType.IM,
      },
    });
  };

  handleEditMember = () => {
    this.setState({
      editMember: this.state.member,
    });
  };

  handleAction = () => {
    this.setState({
      editAction: this.state.member,
    });
  };

  handleSaveMember = () => {
    this.handleCloseDialog();
    this.reload(this.props.number);
  };

  handleClickCert = () => {
    this.setState({ addCertMember: this.state.member });
  };

  handleCloseDialog = () => {
    this.setState({
      dialog: undefined,
      dialogError: undefined,
      dialogSuccess: undefined,
      addCertMember: undefined,
      editMember: undefined,
      editAction: undefined,
    });
  };

  speedBumpIfPendingOrders = (dialog: string, skip: () => void) => () => {
    const { orders = [] } = this.state;
    if (orders.filter(({ status }) => status === Order.Status.PENDING).length) {
      this.setState({ dialog });
    } else {
      skip();
    }
  };

  goToCreateOrder = () => {
    const { member } = this.state;
    if (member) {
      this.props.history.push(`/checkout?memberId=${member.id}`);
    }
  };

  goToPayDues = () => {
    const { member } = this.state;
    if (member) {
      this.props.history.push(`/checkout?memberId=${member.id}&renew=1`);
    }
  };

  goToPayWiki = () => {
    const { member } = this.state;
    if (member) {
      const sku =
        member.memberType !== Member.MemberType.NONE && member.memberType !== Member.MemberType.PROSPECT
          ? 'WIKI-MEMB'
          : 'WIKI-NON';
      this.props.history.push(`/checkout?memberId=${member.id}&sku=${sku}`);
    }
  };

  render() {
    const { member, orders, error } = this.state;

    if (error) {
      return <div className="alert alert-danger">{error}</div>;
    }
    if (!member || !orders) {
      return <Loading />;
    }

    const tabs = [
      { name: 'info', label: 'Info' },
      { name: 'activity', label: 'Activity' },
      { name: 'orders', label: 'Orders', testId: 'usersDetailTabOrders' },
      { name: 'certifications', label: 'Certifications', testId: 'certificationsDetailTabOrders' },
      { name: 'badges', label: 'Badges', testId: 'badgesDetailTabOrders' },
    ];

    return (
      <Page
        title={member.firstName + ' ' + member.lastName + ' (' + member.number + ')'}
        history={this.props.history}
        breadcrumbs={[{ label: 'Users', to: '/users?q=status:active' }]}
        primaryAction={{
          shouldRender:
            acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_MEMBER) ||
            (this.props.appState.user && this.props.appState.user.adminAccess === Member.AdminAccess.CHAPTERS),
          onClick: this.handleClickEdit,
          label: 'Edit User',
        }}
        secondaryActions={[
          {
            shouldRender:
              !member.id && acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_MEMBER),
            label: 'Create Membership',
            iconName: 'id-card-o',
            onClick: this.handleCreateMember,
          },
          {
            shouldRender:
              acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_MEMBER) &&
              (this.props.appState.user && this.props.appState.user.adminAccess !== Member.AdminAccess.CHAPTERSADMIN),
            label: 'Edit Membership',
            iconName: 'id-card-o',
            onClick: this.handleEditMember,
          },
          {
            shouldRender: acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_ORDER),
            label: 'Pay Dues',
            iconName: 'usd',
            onClick: this.speedBumpIfPendingOrders('payDues', this.goToPayDues),
            testId: 'usersDetailActionPayDues',
          },
          {
            shouldRender:
              member.status !== Member.Status.ACTIVE &&
              acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_MEMBER),
            label: 'Reactivate',
            iconName: 'bolt',
            onClick: this.handleOpenActivate,
          },
          {
            shouldRender: acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_ORDER),
            label: 'Buy Certification',
            iconName: 'certificate',
            onClick: this.handleClickCert,
          },
          {
            shouldRender: acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_ORDER),
            label: 'Buy Wiki Access',
            iconName: 'wikipedia-w',
            onClick: this.goToPayWiki,
          },
          {
            shouldRender: acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_ORDER),
            label: 'Create Order',
            iconName: 'shopping-cart',
            onClick: this.speedBumpIfPendingOrders('createOrder', this.goToCreateOrder),
            testId: 'usersDetailActionCreateOrder',
          },
          {
            shouldRender:
              this.props.appState.user && this.props.appState.user.adminAccess !== Member.AdminAccess.CHAPTERSADMIN,
            label: 'Send Password Reset',
            iconName: 'envelope-o',
            onClick: this.handleOpenSendReset,
          },
          {
            shouldRender: acl.hasPermission(this.props.appState, UserPermissions.Permissions.IMPORT_ELIGIBILITY),
            label: 'Resend Learning Program Email',
            iconName: 'envelope-o',
            onClick: this.handleOpenSendEligibleEmail,
          },
        ]}
      >
        <Card padding="1rem" marginBottom="2rem" background="#fff">
          <Row>
            <Block>
              <MediaImage
                path={member.imagePath}
                size="sm"
                className="rounded-circle"
                style={{ border: '1px solid #eee' }}
              />
            </Block>

            <Row justifyContent="space-around" alignItems="center" marginLeft=".5rem">
              <Block className="h4" marginRight=".5rem">
                {member.firstName + ' ' + member.lastName}
              </Block>
              {member.status === Member.Status.ACTIVE && (
                <Badge type="success" text="Active" style={{ marginRight: '.5rem' }} />
              )}{' '}
              {member.status === Member.Status.EXPIRED && (
                <Badge type="danger" text="Expired" style={{ marginRight: '.5rem' }} />
              )}{' '}
              {member.status === Member.Status.INACTIVE && (
                <Badge type="danger" text="Inactive" style={{ marginRight: '.5rem' }} />
              )}{' '}
              {member.adminAccess !== Member.AdminAccess.NONE && (
                <Badge
                  type="primary"
                  text={acl.toAdminAccessLabel(member.adminAccess!)}
                  style={{ marginRight: '.5rem' }}
                />
              )}
            </Row>
            {this.props.history.location.pathname.includes('certifications') ? (
              <Row justifyContent="space-around" alignItems="center" marginLeft="auto">
                <Block marginLeft="1rem">
                  <ActionButton label="Approve" onClick={this.handleAction} primary={true} />
                </Block>
                <Block marginLeft="1rem">
                  <ActionButton label="Deny" onClick={this.handleOpenLDDenyEmail} primary={true} />
                </Block>
              </Row>
            ) : (
              <></>
            )}
          </Row>

          <HistoryTabs
            tabs={tabs}
            history={this.props.history}
            location={this.props.location}
            basePath={'/users/' + member.number}
          >
            <TabPane tabId="info">
              <UsersDetailInfo
                member={member}
                chapterPositions={this.state.chapterPosition}
                appState={this.props.appState}
                onUpdateTags={this.handleUpdateTags}
                onUpdateAssignedTags={this.handleUpdateAssignedTags}
                onUpdateInternalTags={this.handleUpdateInternalTags}
              />
            </TabPane>
            <TabPane tabId="activity">
              <ActivityFeed
                appState={this.props.appState}
                entityActions={this.props.entityActions}
                entityId={this.state.member!.id!}
                entityType="USER"
                onSave={this.handleReload}
              />
            </TabPane>
            <TabPane tabId="orders">
              <MembersOrders
                appState={this.props.appState}
                member={member}
                entityActions={this.props.entityActions}
                onEdit={this.handleClickEdit}
              />
            </TabPane>
            <TabPane tabId="certifications">
              <MembersCertifications
                appState={this.props.appState}
                member={member}
                entityActions={this.props.entityActions}
                onEdit={this.handleClickEdit}
              />
            </TabPane>
            <TabPane tabId="badges">
              <MembersBadges
                appState={this.props.appState}
                member={member}
                entityActions={this.props.entityActions}
                onEdit={this.handleClickEdit}
              />
            </TabPane>
          </HistoryTabs>
        </Card>
        <ConfirmDialog
          title="Send Password Reset"
          message={'Are you sure you want to send a password reset email to ' + member.email + '?'}
          isOpen={this.state.dialog === 'sendReset'}
          onConfirm={this.handleSendReset}
          onCancel={this.handleCloseDialog}
          isLoading={this.state.dialogLoading}
          success={this.state.dialogSuccess}
          error={this.state.dialogError}
          loadingMessage="Sending..."
        />
        <ConfirmDialog
          title="Send Application Denied E-mail"
          message={'Are you sure you want to send a application denied email to ' + member.email + '?'}
          isOpen={this.state.dialog === 'sendApplicationDenied'}
          onConfirm={this.handleSendLDDenyEmail}
          onCancel={this.handleCloseDialog}
          isLoading={this.state.dialogLoading}
          success={this.state.dialogSuccess}
          error={this.state.dialogError}
          loadingMessage="Sending..."
        />
        <DialogResendEligibleEmail
          isOpen={this.state.dialog === 'sendEligibleEmail'}
          memberId={member.id}
          onCancel={this.handleCloseDialog}
          onConfirm={this.handleCloseDialog}
        />
        <ConfirmDialog
          title="Reactivate"
          message={'Are you sure you want to reactivate ' + member.firstName + ' ' + member.lastName + '?'}
          isOpen={this.state.dialog === 'activate'}
          onConfirm={this.handleActivate}
          onCancel={this.handleCloseDialog}
          isLoading={this.state.dialogLoading}
          success={this.state.dialogSuccess}
          error={this.state.dialogError}
          loadingMessage="Activating..."
        />
        <ConfirmDialog
          title="Create Order"
          message={
            <span>
              Are you sure you want to create an order? This member has{' '}
              <Link onClick={this.handleCloseDialog} to={`/users/${member.number}/orders`}>
                pending orders
              </Link>.
            </span>
          }
          isOpen={this.state.dialog === 'createOrder'}
          onConfirm={this.goToCreateOrder}
          onCancel={this.handleCloseDialog}
        />
        <ConfirmDialog
          title="Pay Dues"
          message={
            <span>
              Are you sure you want to pay dues? This member has{' '}
              <Link onClick={this.handleCloseDialog} to={`/users/${member.number}/orders`}>
                pending orders
              </Link>.
            </span>
          }
          isOpen={this.state.dialog === 'payDues'}
          onConfirm={this.goToPayDues}
          onCancel={this.handleCloseDialog}
        />
        <MemberDialog
          appState={this.props.appState}
          member={this.state.editMember}
          isOrg={false}
          onCancel={this.handleCloseDialog}
          onSave={this.handleSaveMember}
        />
        <MemberLDActionDialog
          appState={this.props.appState}
          member={this.state.editAction}
          allProducts={this.state.allProducts}
          isOrg={false}
          onCancel={this.handleCloseDialog}
          onSave={this.handleSaveMember}
        />
        <CertDialog user={this.state.addCertMember} onCancel={this.handleCloseDialog} history={this.props.history} />
      </Page>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/Users/UsersDetail.tsx